import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { adminCategoriesApi, adminProductsApi, Category, CreateCategoryPayload } from '../services/adminApi';
import DataTable from './components/DataTable';
import Modal from './components/Modal';

const inputClass =
  'w-full px-3 py-2 border border-cream-200 rounded-lg text-sm bg-cream-50 text-coffee-200 outline-none focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-all';
const labelClass = 'block text-sm font-medium text-coffee-200 mb-1';
const errorClass = 'mt-1 text-xs text-red-600';

const categoryFormSchema = z.object({
  id: z.string().min(2, 'Trop court').max(50).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Minuscules et tirets uniquement (ex: huiles-essentielles)'),
  label_fr: z.string().min(2, 'Trop court').max(100),
  label_en: z.string().min(2, 'Trop court').max(100),
  description_fr: z.string().max(1000).optional(),
  description_en: z.string().max(1000).optional(),
  image: z.string().optional(),
  sort_order: z.number().int().optional(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;

const emptyDefaults: CategoryFormValues = { id: '', label_fr: '', label_en: '', description_fr: '', description_en: '', image: '', sort_order: 0 };

const slugify = (v: string) =>
  v
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents: "Résines" -> "Resines"
    .toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// ── Cover image upload (mirrors AdminProducts' own field, kept local since
// the categories module is small enough not to warrant a shared file yet) ──
const ImageUploadField: React.FC<{ value?: string; onChange: (path: string) => void; onUnauthorized: () => void }> = ({ value, onChange, onUnauthorized }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const { path } = await adminProductsApi.uploadImage(file);
      onChange(path);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message || "Erreur lors de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      {value ? (
        <img src={value} alt="" className="h-14 w-14 shrink-0 rounded-lg border border-cream-200 bg-cream-200 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
      ) : (
        <div className="h-14 w-14 shrink-0 rounded-lg bg-cream-200" />
      )}
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-lg bg-cream-200 px-4 py-2 text-sm font-medium text-coffee-200 transition-colors hover:bg-cream-300 disabled:opacity-50">
            {uploading ? 'Envoi...' : value ? "Changer l'image" : 'Choisir une image'}
          </button>
          {value && !uploading && (
            <button type="button" onClick={() => onChange('')} className="px-3 py-2 text-sm text-red-500 hover:text-red-700">Retirer</button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
        <p className="truncate text-xs text-coffee-100">{value || 'Aucune image sélectionnée'}</p>
      </div>
    </div>
  );
};

const CategoryFormModal: React.FC<{
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
  onUnauthorized: () => void;
}> = ({ category, onClose, onSaved, onUnauthorized }) => {
  const isEdit = !!category;
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: category
      ? { id: category.id, label_fr: category.label_fr, label_en: category.label_en, description_fr: category.description_fr ?? '', description_en: category.description_en ?? '', image: category.image ?? '', sort_order: category.sort_order }
      : emptyDefaults,
  });

  const image = watch('image');
  const labelFr = watch('label_fr');

  // Auto-suggest the id from the French label while creating — never once it exists.
  const idTouchedRef = useRef(isEdit);
  useEffect(() => {
    if (!isEdit && !idTouchedRef.current) setValue('id', slugify(labelFr || ''));
  }, [labelFr, isEdit, setValue]);

  const onSubmit = async (values: CategoryFormValues) => {
    setSubmitting(true);
    const payload: CreateCategoryPayload = {
      id: values.id,
      label_fr: values.label_fr,
      label_en: values.label_en,
      description_fr: values.description_fr?.trim() || null,
      description_en: values.description_en?.trim() || null,
      image: values.image?.trim() || null,
      sort_order: values.sort_order ?? 0,
    };
    try {
      if (isEdit) {
        await adminCategoriesApi.update(category.id, payload);
        toast.success('Catégorie mise à jour');
      } else {
        await adminCategoriesApi.create(payload);
        toast.success('Catégorie créée');
      }
      onSaved();
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message === 'Category id already exists' ? 'Cet identifiant existe déjà' : (e.message || "Erreur lors de l'enregistrement"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-xl">
      <div className="mb-5 flex items-start justify-between">
        <h2 className="font-display text-xl font-bold text-coffee-200">{isEdit ? `Modifier — ${category!.label_fr}` : 'Nouvelle catégorie'}</h2>
        <button onClick={onClose} className="text-2xl leading-none text-coffee-100 hover:text-coffee-200" aria-label="Fermer">×</button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Nom (FR) *</label>
            <input {...register('label_fr')} className={inputClass} placeholder="Huiles Essentielles" />
            {errors.label_fr && <p className={errorClass}>{errors.label_fr.message}</p>}
          </div>
          <div>
            <label className={labelClass}>Nom (EN) *</label>
            <input {...register('label_en')} className={inputClass} placeholder="Essential Oils" />
            {errors.label_en && <p className={errorClass}>{errors.label_en.message}</p>}
          </div>
        </div>

        <div>
          <label className={labelClass}>Identifiant (slug) *</label>
          <input
            {...register('id', { onChange: () => { idTouchedRef.current = true; } })}
            className={inputClass}
            placeholder="huiles-essentielles"
            disabled={isEdit}
          />
          {errors.id && <p className={errorClass}>{errors.id.message}</p>}
          {isEdit && <p className="mt-1 text-xs text-coffee-100">L'identifiant ne peut pas être modifié après création.</p>}
        </div>

        <div>
          <label className={labelClass}>Image</label>
          <ImageUploadField value={image} onChange={(v) => setValue('image', v)} onUnauthorized={onUnauthorized} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Description (FR)</label>
            <textarea {...register('description_fr')} rows={3} className={`${inputClass} resize-none`} />
          </div>
          <div>
            <label className={labelClass}>Description (EN)</label>
            <textarea {...register('description_en')} rows={3} className={`${inputClass} resize-none`} />
          </div>
        </div>

        <div className="max-w-[160px]">
          <label className={labelClass}>Ordre d'affichage</label>
          <input type="number" step="1" {...register('sort_order', { valueAsNumber: true })} className={inputClass} />
        </div>

        <div className="flex gap-3 border-t border-cream-200 pt-4">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-cream-200 py-2.5 text-sm text-coffee-100 transition-colors hover:bg-cream-100">
            Annuler
          </button>
          <button type="submit" disabled={submitting} className="flex-1 rounded-lg bg-forest-200 py-2.5 text-sm font-semibold text-cream-50 transition-colors hover:bg-forest-100 disabled:cursor-not-allowed disabled:opacity-50">
            {submitting ? 'Enregistrement...' : isEdit ? 'Enregistrer' : 'Créer la catégorie'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const DeleteConfirmModal: React.FC<{
  category: Category;
  onClose: () => void;
  onDeleted: () => void;
  onUnauthorized: () => void;
}> = ({ category, onClose, onDeleted, onUnauthorized }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminCategoriesApi.delete(category.id);
      toast.success('Catégorie supprimée');
      onDeleted();
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') { onUnauthorized(); return; }
      toast.error(e.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-sm">
      <h2 className="mb-2 font-display text-lg font-bold text-coffee-200">Supprimer cette catégorie ?</h2>
      <p className="mb-5 text-sm text-coffee-100">
        <span className="font-medium text-coffee-200">{category.label_fr}</span> sera définitivement supprimée. Si des produits l'utilisent encore, la suppression sera refusée.
      </p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 rounded-lg border border-cream-200 py-2.5 text-sm text-coffee-100 hover:bg-cream-100">Annuler</button>
        <button onClick={handleDelete} disabled={deleting} className="flex-1 rounded-lg bg-red-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50">
          {deleting ? 'Suppression...' : 'Supprimer'}
        </button>
      </div>
    </Modal>
  );
};

interface Props {
  onLogout: () => void;
  onCountChange?: (count: number) => void;
}

const AdminCategories: React.FC<Props> = ({ onLogout, onCountChange }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [formTarget, setFormTarget] = useState<'new' | Category | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await adminCategoriesApi.getAll();
      const sorted = [...data].sort((a, b) => a.sort_order - b.sort_order || a.label_fr.localeCompare(b.label_fr));
      setCategories(sorted);
      onCountChange?.(sorted.length);
    } catch (e: any) {
      if (e.message === 'UNAUTHORIZED') onLogout();
      else toast.error('Impossible de charger les catégories');
    } finally {
      setLoading(false);
    }
  }, [onLogout, onCountChange]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const columns = useMemo<ColumnDef<Category, any>[]>(() => [
    {
      accessorKey: 'label_fr',
      header: 'Catégorie',
      cell: (info) => (
        <div className="flex items-center gap-3">
          {info.row.original.image ? (
            <img src={info.row.original.image} alt="" className="h-10 w-10 shrink-0 rounded-lg bg-cream-200 object-cover" onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
          ) : (
            <div className="h-10 w-10 shrink-0 rounded-lg bg-cream-200" />
          )}
          <div className="min-w-0">
            <div className="truncate font-medium text-coffee-200">{info.getValue() as string}</div>
            <div className="truncate text-xs text-coffee-100">{info.row.original.id}</div>
          </div>
        </div>
      ),
    },
    { accessorKey: 'label_en', header: 'Nom (EN)', cell: (info) => <span className="text-coffee-100">{info.getValue() as string}</span> },
    { accessorKey: 'sort_order', header: 'Ordre', cell: (info) => <span className="text-coffee-100">{info.getValue() as number}</span> },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: (info) => (
        <div className="flex gap-2">
          <button onClick={() => setFormTarget(info.row.original)} className="rounded-lg bg-forest-200 px-3 py-1.5 text-xs font-semibold text-cream-50 transition-colors hover:bg-forest-100">Modifier</button>
          <button onClick={() => setDeleteTarget(info.row.original)} className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50">Supprimer</button>
        </div>
      ),
    },
  ], []);

  return (
    <div>
      <DataTable
        columns={columns}
        data={categories}
        loading={loading}
        emptyMessage="Aucune catégorie"
        searchPlaceholder="Rechercher une catégorie..."
        toolbarExtra={
          <button onClick={() => setFormTarget('new')} className="rounded-full bg-forest-200 px-5 py-2 text-sm font-medium text-cream-50 transition-colors hover:bg-forest-100">
            + Ajouter une catégorie
          </button>
        }
      />

      {formTarget && (
        <CategoryFormModal
          category={formTarget === 'new' ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onSaved={() => { setFormTarget(null); fetchCategories(); }}
          onUnauthorized={onLogout}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          category={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); fetchCategories(); }}
          onUnauthorized={onLogout}
        />
      )}
    </div>
  );
};

export default AdminCategories;
